import React from "react";
import { Checkbox, FormControlLabel, Stack } from "@mui/material";

import ConfirmDialog from "../../shared/ConfirmDialog";
import MarkerTypeOption from "./MarkerTypeOption";
import PhaseLineFields from "./PhaseLineFields";
import ExternalEventFields from "./ExternalEventFields";
import { useMarkerForm } from "./useMarkerForm";
import type { MarkerDialogProps, MarkerFieldsProps, MarkerType } from "./MarkerDialog.types";

interface MarkerVariant {
  /** Fields rendered below the type selector. */
  Fields: React.FC<MarkerFieldsProps>;
  /** Primary action label — the Figma spec names the action per type. */
  confirmLabel: string;
  /**
   * Whether the variant offers "create more" (keep the dialog open after
   * saving). External events only — phase lines don't offer it.
   */
  supportsCreateMore: boolean;
}

/**
 * Per-type form variant. Typed as a full `Record<MarkerType, …>` so adding a
 * marker type without its variant is a compile error.
 */
const VARIANT_BY_TYPE: Record<MarkerType, MarkerVariant> = {
  phaseChange: {
    Fields: PhaseLineFields,
    confirmLabel: "Add Phase Line",
    supportsCreateMore: false,
  },
  externalEvent: {
    Fields: ExternalEventFields,
    confirmLabel: "Add Marker",
    supportsCreateMore: true,
  },
};

/**
 * Dialog for adding a graph marker to a target's data chart. The clinician
 * picks a marker type — a phase line (protocol/phase transition) or an
 * external event (medication, illness, clinician change) — and fills the
 * fields that type needs; each type renders its own variant.
 *
 * Editing reopens the same form with the marker's values: its position (session
 * or date) and the targets it applies to are editable; only the type is locked,
 * since the API can't change it.
 *
 * The captured values are handed to `onSubmit`; the caller maps them to the
 * markers API and persists them.
 *
 * @param props
 * @param props.open      - Controls dialog visibility
 * @param props.onClose   - Dismiss handler (no save)
 * @param props.sessions  - Sessions available to anchor the marker to
 * @param props.onSubmit   - Called with the captured values on confirm
 * @param props.isLoading  - Disables actions while a save is in flight
 * @param props.resetToken - Bumped by the caller to clear label/notes after a
 *                           "create more" save
 * @param [props.mode]          - "create" (default) or "edit"
 * @param [props.initialValues] - Edit mode: the marker's current values
 * @param [props.targetsContext] - Enables applying the marker to other targets
 */
const MarkerDialog: React.FC<MarkerDialogProps> = ({
  open,
  onClose,
  sessions,
  onSubmit,
  isLoading = false,
  resetToken = 0,
  mode = "create",
  initialValues,
  targetsContext,
}) => {
  const isEdit = mode === "edit";

  const { values, setField, isValid, isDirty, labelInputRef } = useMarkerForm({
    open,
    hasSessions: sessions.length > 0,
    resetToken,
    initialValues,
  });

  const isPhaseChange = values.type === "phaseChange";
  const variant = VARIANT_BY_TYPE[values.type];
  const { Fields } = variant;
  // "Create more" is for adding markers in a row — not offered while editing.
  const supportsCreateMore = variant.supportsCreateMore && !isEdit;

  const handleConfirm = () => {
    if (!isValid || !isDirty) return;
    // "Create more" only exists on variants that offer it.
    // Only include `createMore` in the submit payload if the variant supports it.
    onSubmit({ ...values, createMore: supportsCreateMore && values.createMore });
  };

  return (
    <ConfirmDialog
      open={open}
      title={isEdit ? "Edit Marker" : "Add Marker"}
      subtitle="Mark a phase change or external event on the data graph."
      confirmLabel={isEdit ? "Save Changes" : variant.confirmLabel}
      onClose={onClose}
      onConfirm={handleConfirm}
      isLoading={isLoading}
      // An edit with nothing changed would just re-save the same marker.
      confirmDisabled={!isValid || !isDirty}
      actionsStart={
        supportsCreateMore ? (
          <FormControlLabel
            control={
              <Checkbox
                checked={values.createMore}
                onChange={(event) => setField("createMore", event.target.checked)}
                size="small"
                inputProps={{ "aria-label": "Create more" }}
              />
            }
            label="Create more"
          />
        ) : null
      }
    >
      <Stack sx={{ pt: 7 }} gap={7}>

        {/* Marker type selector */}
        <Stack gap={3} role="radiogroup" aria-label="Marker type">
          <MarkerTypeOption
            selected={isPhaseChange}
            title="Phase Line"
            description="Changes to teaching procedures"
            sample="solid"
            disabled={isEdit}
            onSelect={() => setField("type", "phaseChange")}
          />
          <MarkerTypeOption
            selected={!isPhaseChange}
            title="External Event"
            description="Medication, illness, clinician change"
            sample="dashed"
            disabled={isEdit}
            onSelect={() => setField("type", "externalEvent")}
          />
        </Stack>

        {/* Type-specific fields */}
        <Fields
          values={values}
          sessions={sessions}
          onFieldChange={setField}
          labelInputRef={labelInputRef}
          targetsContext={targetsContext}
        />
      </Stack>
    </ConfirmDialog>
  );
};

export default MarkerDialog;

export { default as MarkerTypeOption } from "./MarkerTypeOption";
export { default as PhaseLineFields } from "./PhaseLineFields";
export { default as ExternalEventFields } from "./ExternalEventFields";
export * from "./MarkerDialog.types";
